import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { UserCircle, Plus, Pencil, Trash2, Phone, Mail, Award, Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POSITIONS = ["管理者", "サービス管理責任者", "支援員", "事務員", "看護師", "相談員"];
const EMP_TYPES = ["正社員", "パート", "契約社員", "派遣"];
const QUALS = ["介護福祉士", "社会福祉士", "精神保健福祉士", "ヘルパー2級", "初任者研修", "実務者研修", "看護師", "准看護師", "保育士"];

const INITIAL = { name: '', position: 'support_staff', employment_type: '正社員', phone: '', email: '', emergency_contact: '', emergency_phone: '', qualifications: [], hire_date: '', notes: '' };

export default function StaffPage() {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(INITIAL);
  const [saving, setSaving] = useState(false);
  const [filterPosition, setFilterPosition] = useState('all');

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${API}/staff`);
      setStaffList(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openNew = () => { setEditingId(null); setForm({ ...INITIAL, position: '支援員' }); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, position: s.position, employment_type: s.employment_type, phone: s.phone || '', email: s.email || '', emergency_contact: s.emergency_contact || '', emergency_phone: s.emergency_phone || '', qualifications: s.qualifications || [], hire_date: s.hire_date || '', notes: s.notes || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/staff/${editingId}`, form);
      } else {
        await axios.post(`${API}/staff`, form);
      }
      setDialogOpen(false);
      fetchStaff();
    } catch (e) {
      alert('保存に失敗: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/staff/${id}`); fetchStaff(); }
    catch (e) { alert('削除に失敗: ' + (e.response?.data?.detail || e.message)); }
  };

  const toggleQual = (q) => {
    setForm(prev => ({
      ...prev,
      qualifications: prev.qualifications.includes(q)
        ? prev.qualifications.filter(x => x !== q)
        : [...prev.qualifications, q]
    }));
  };

  const filtered = staffList.filter(s => filterPosition === 'all' || s.position === filterPosition);

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="staff-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">職員管理</h1>
          <p className="text-muted-foreground mt-1">職員数: {staffList.length}名（在籍: {staffList.filter(s=>s.is_active !== false).length}名）</p>
        </div>
        <Button className="btn-active" onClick={openNew} data-testid="new-staff-button">
          <Plus size={18} className="mr-2" />新規登録
        </Button>
      </div>

      <Card className="card-surface">
        <CardContent className="pt-4 pb-4">
          <Select value={filterPosition} onValueChange={setFilterPosition}>
            <SelectTrigger className="w-48" data-testid="filter-position"><SelectValue placeholder="職種" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">すべての職種</SelectItem>
              {POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Card className="card-surface"><CardContent className="py-12 text-center">
          <UserCircle size={48} className="mx-auto mb-4 text-muted-foreground/50" />
          <p className="text-muted-foreground">職員が登録されていません</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(s => (
            <Card key={s.id} className="card-surface hover:shadow-md transition-shadow" data-testid={`staff-card-${s.id}`}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <UserCircle size={24} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.position} / {s.employment_type}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`}><Pencil size={14} /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="icon" variant="ghost" className="text-destructive" data-testid={`delete-staff-${s.id}`}><Trash2 size={14} /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>職員を削除しますか？</AlertDialogTitle><AlertDialogDescription>この操作は取り消せません。</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>キャンセル</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(s.id)}>削除</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <div className="space-y-1.5 text-sm">
                  {s.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone size={14} />{s.phone}</div>}
                  {s.email && <div className="flex items-center gap-2 text-muted-foreground"><Mail size={14} />{s.email}</div>}
                  {s.hire_date && <div className="flex items-center gap-2 text-muted-foreground"><Briefcase size={14} />入職: {s.hire_date}</div>}
                </div>
                {s.qualifications?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {s.qualifications.map(q => (
                      <span key={q} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">{q}</span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? '職員編集' : '新規職員登録'}</DialogTitle>
            <DialogDescription>職員情報を入力してください</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>氏名 *</Label><Input value={form.name} onChange={e => setForm(p=>({...p, name: e.target.value}))} required data-testid="staff-name-input" /></div>
              <div className="space-y-2"><Label>職種 *</Label>
                <Select value={form.position} onValueChange={v => setForm(p=>({...p, position: v}))}>
                  <SelectTrigger data-testid="staff-position-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>雇用形態</Label>
                <Select value={form.employment_type} onValueChange={v => setForm(p=>({...p, employment_type: v}))}>
                  <SelectTrigger data-testid="staff-emptype-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{EMP_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>入職日</Label><Input type="date" value={form.hire_date} onChange={e => setForm(p=>({...p, hire_date: e.target.value}))} data-testid="staff-hiredate-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>電話番号</Label><Input value={form.phone} onChange={e => setForm(p=>({...p, phone: e.target.value}))} data-testid="staff-phone-input" /></div>
              <div className="space-y-2"><Label>メール</Label><Input value={form.email} onChange={e => setForm(p=>({...p, email: e.target.value}))} data-testid="staff-email-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>緊急連絡先（氏名）</Label><Input value={form.emergency_contact} onChange={e => setForm(p=>({...p, emergency_contact: e.target.value}))} data-testid="staff-emergency-name-input" /></div>
              <div className="space-y-2"><Label>緊急連絡先（電話）</Label><Input value={form.emergency_phone} onChange={e => setForm(p=>({...p, emergency_phone: e.target.value}))} data-testid="staff-emergency-phone-input" /></div>
            </div>
            <div className="space-y-2">
              <Label>資格</Label>
              <div className="flex flex-wrap gap-2">
                {QUALS.map(q => (
                  <button key={q} type="button" onClick={() => toggleQual(q)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${form.qualifications.includes(q) ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary border-border hover:border-primary/50'}`}
                    data-testid={`qual-${q}`}>{q}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2"><Label>備考</Label><Textarea value={form.notes} onChange={e => setForm(p=>({...p, notes: e.target.value}))} rows={2} data-testid="staff-notes-input" /></div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
              <Button className="btn-active" onClick={handleSave} disabled={saving || !form.name} data-testid="staff-save-button">{saving ? '保存中...' : (editingId ? '更新' : '登録')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
